/**
 * ActionGuard - Permission-based Action Wrapper Component
 * Wraps action buttons and controls visibility/enablement based on user permissions
 *
 * Usage:
 * <ActionGuard action="canDeleteUsers">
 *   <Button onClick={handleDelete}>Delete User</Button>
 * </ActionGuard>
 *
 * <ActionGuard resource="members" operation="delete" ownerId={user.id}>
 *   <Button onClick={handleDelete}>Delete</Button>
 * </ActionGuard>
 */

import { ReactNode } from 'react'
import {
  usePermissions,
  ActionPermissions,
  ResourcePermissions,
  ResourceCRUD,
} from './permission-context'
import { createLogger } from '@/shared/lib/logger'

const logger = createLogger('action-guard')

type ActionGuardMode = 'hide' | 'disable'

interface ActionGuardBaseProps {
  /** Children to render if permission is granted */
  children: ReactNode
  /** Mode: 'hide' removes element, 'disable' shows but disables it */
  mode?: ActionGuardMode
  /** Optional fallback to render when permission denied (only for 'hide' mode) */
  fallback?: ReactNode
  /** Optional tooltip to show when disabled */
  disabledTooltip?: string
}

interface ActionPermissionGuardProps extends ActionGuardBaseProps {
  /** Action permission to check (e.g., 'canDeleteUsers') */
  action: keyof ActionPermissions
  resource?: never
  operation?: never
  ownerId?: never
}

interface ResourcePermissionGuardProps extends ActionGuardBaseProps {
  /** Resource to check permission for */
  resource: keyof ResourcePermissions
  /** CRUD operation to check */
  operation: keyof ResourceCRUD
  /** Owner ID for scope checking (required for 'own' scope resources) */
  ownerId?: string
  action?: never
}

type ActionGuardProps = ActionPermissionGuardProps | ResourcePermissionGuardProps

/**
 * ActionGuard Component
 *
 * Provides declarative permission checking for UI actions.
 * Supports two modes:
 * - Action-based: Check specific action permissions (canDeleteUsers, etc.)
 * - Resource-based: Check CRUD permissions on resources (members:delete, agents:create, etc.)
 */
export function ActionGuard(props: ActionGuardProps) {
  const { children, mode = 'hide', fallback = null, disabledTooltip } = props
  const { canPerformAction, canAccessResource, permissions } = usePermissions()

  // Determine if permission is granted
  let hasPermission = false

  if ('action' in props && props.action) {
    // Action-based permission check
    hasPermission = canPerformAction(props.action)
    logger.debug('Action permission check', {
      action: props.action,
      hasPermission,
      role: permissions?.role,
    })
  } else if ('resource' in props && props.resource && props.operation) {
    // Resource-based permission check
    hasPermission = canAccessResource(props.resource, props.operation, props.ownerId)
    logger.debug('Resource permission check', {
      resource: props.resource,
      operation: props.operation,
      ownerId: props.ownerId,
      hasPermission,
      role: permissions?.role,
    })
  }

  // Handle permission denied
  if (!hasPermission) {
    if (mode === 'hide') {
      return <>{fallback}</>
    }

    // mode === 'disable'
    return (
      <DisabledWrapper tooltip={disabledTooltip}>
        {children}
      </DisabledWrapper>
    )
  }

  // Permission granted - render children as-is
  return <>{children}</>
}

/**
 * DisabledWrapper - Wraps children and disables them
 */
interface DisabledWrapperProps {
  children: ReactNode
  tooltip?: string
}

function DisabledWrapper({ children, tooltip }: DisabledWrapperProps) {
  return (
    <div
      className="relative inline-block"
      // eslint-disable-next-line i18next/no-literal-string
      title={tooltip || 'You do not have permission to perform this action'}
    >
      <div className="pointer-events-none opacity-50">
        {children}
      </div>
    </div>
  )
}

/**
 * OwnerOnly - Renders children only for organization owners
 */
export function OwnerOnly({ children, fallback = null }: { children: ReactNode; fallback?: ReactNode }) {
  const { isOwner } = usePermissions()

  if (!isOwner()) {
    return <>{fallback}</>
  }

  return <>{children}</>
}

/**
 * AdminOnly - Renders children only for admins and owners
 */
export function AdminOnly({ children, fallback = null }: { children: ReactNode; fallback?: ReactNode }) {
  const { isOwner, isAdmin } = usePermissions()

  if (!isOwner() && !isAdmin()) {
    return <>{fallback}</>
  }

  return <>{children}</>
}

/**
 * ManagerOrAbove - Renders children for managers, admins, and owners
 */
export function ManagerOrAbove({ children, fallback = null }: { children: ReactNode; fallback?: ReactNode }) {
  const { isOwner, isAdmin, isManager } = usePermissions()

  if (!isOwner() && !isAdmin() && !isManager()) {
    return <>{fallback}</>
  }

  return <>{children}</>
}

/**
 * NotReadOnly - Renders children only if user is not read-only (auditor)
 */
export function NotReadOnly({ children, fallback = null }: { children: ReactNode; fallback?: ReactNode }) {
  const { isReadOnly } = usePermissions()

  if (isReadOnly()) {
    return <>{fallback}</>
  }

  return <>{children}</>
}

/**
 * CanInvite - Renders children only if user can invite members
 */
export function CanInvite({ children, fallback = null }: { children: ReactNode; fallback?: ReactNode }) {
  const { canInviteMembers } = usePermissions()

  if (!canInviteMembers()) {
    return <>{fallback}</>
  }

  return <>{children}</>
}

/**
 * CanManageMembers - Renders children only if user can change roles or delete users
 */
export function CanManageMembers({ children, fallback = null }: { children: ReactNode; fallback?: ReactNode }) {
  const { canManageMembers } = usePermissions()

  if (!canManageMembers()) {
    return <>{fallback}</>
  }

  return <>{children}</>
}

/**
 * CanDeleteOrganization - Renders children only if user can delete the organization
 */
export function CanDeleteOrganization({ children, fallback = null }: { children: ReactNode; fallback?: ReactNode }) {
  const { canDeleteOrganization } = usePermissions()

  if (!canDeleteOrganization()) {
    return <>{fallback}</>
  }

  return <>{children}</>
}

/**
 * useActionPermission - Hook for imperative permission checks
 *
 * Usage:
 * const canDelete = useActionPermission('canDeleteUsers')
 * if (canDelete) { ... }
 */
export function useActionPermission(action: keyof ActionPermissions): boolean {
  const { canPerformAction } = usePermissions()
  return canPerformAction(action)
}

/**
 * useResourcePermission - Hook for imperative resource permission checks
 *
 * Usage:
 * const canDeleteMember = useResourcePermission('members', 'delete', memberId)
 * if (canDeleteMember) { ... }
 */
export function useResourcePermission(
  resource: keyof ResourcePermissions,
  operation: keyof ResourceCRUD,
  ownerId?: string
): boolean {
  const { canAccessResource } = usePermissions()
  return canAccessResource(resource, operation, ownerId)
}
